import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog, DialogContent, Button, Box, Typography, TextField, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Paper, Autocomplete, CircularProgress, Divider, Chip,
  Tooltip, Alert, alpha, useTheme, Stack, Card, CardContent,
  InputAdornment, LinearProgress,
} from '@mui/material';
import {
  Add, Delete, Save, Close, Receipt as ReceiptIcon,
  Person as PersonIcon, AccountBalance as AccountIcon,
  Category as CategoryIcon, Scale as ScaleIcon,
  Business as BranchIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { custodySettlementService, expenseCategoryService } from '../../services/custodySettlements.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { custodiesService } from '../../services/custodies.service';
import { branchesService } from '../../services/branches.service';
import {
  CustodySettlement, CreateCustodySettlement, UpdateCustodySettlement,
  CreateCustodySettlementDetail, CustodyExpenseCategory,
} from '../../types/custodySettlements.types';
import { Item } from '../../types/items.types';
import { Branch } from '../../types/branches.types';
import axios from '../../services/axiosInstance';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

// ============================================================
// Translations
// ============================================================
const translations = {
  en: {
    addTitle: 'New Custody Settlement',
    editTitle: 'Edit Custody Settlement',
    viewTitle: 'View Custody Settlement',
    basicInfo: 'Basic Information',
    custody: 'Custody',
    selectCustody: 'Select custody...',
    settlementDate: 'Settlement Date',
    settlementNumber: 'Settlement #',
    currentBalance: 'Current Balance',
    branch: 'Branch',
    selectBranch: 'Select branch...',
    defaultBranch: 'Default Branch',
    costCenter: 'Cost Center',
    tabOperational: 'Operational Items',
    tabAdministrative: 'Administrative Expenses',
    tabAssets: 'Fixed Assets',
    assetCategory: 'Asset Category',
    assetName: 'Asset Name',
    assetNameAr: 'Asset Name (AR)',
    selectAssetCategory: 'Select asset category',
    item: 'Item',
    selectItem: 'Select item...',
    unit: 'Unit',
    selectUnit: 'Select unit...',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    amount: 'Amount',
    category: 'Expense Category',
    selectCategory: 'Select category...',
    account: 'Account',
    receiptNumber: 'Receipt #',
    receiptDate: 'Receipt Date',
    description: 'Description',
    addLine: 'Add Line',
    totalExpenses: 'Total Expenses',
    returnedAmount: 'Returned Amount',
    netSettlement: 'Net Settlement',
    notes: 'Notes',
    save: 'Save Settlement',
    cancel: 'Cancel',
    required: 'Required',
    optional: 'Optional',
    saved: 'Settlement saved successfully',
    updated: 'Settlement updated successfully',
    saveFailed: 'Failed to save settlement',
    balanceWarning: 'Total expenses + returned amount exceed custody balance!',
    noItems: 'Add at least one expense line',
    kwd: 'KWD',
    dkw: 'د.ك',
    autoAccount: 'Auto from category',
    autoCostCenter: 'Auto from branch',
    returnAccount: 'Return Account',
    selectReturnAccount: 'Select return account...',
    returnAccountRequired: 'Return account is required when returned amount > 0',
    permissionDenied: 'Permission denied',
  },
  ar: {
    addTitle: 'كشف تسوية عهدة جديد',
    editTitle: 'تعديل كشف تسوية عهدة',
    viewTitle: 'عرض كشف تسوية عهدة',
    basicInfo: 'البيانات الأساسية',
    custody: 'العهدة',
    selectCustody: 'اختر العهدة...',
    settlementDate: 'تاريخ التسوية',
    settlementNumber: 'رقم الكشف',
    currentBalance: 'الرصيد الحالي',
    branch: 'الفرع',
    selectBranch: 'اختر الفرع...',
    defaultBranch: 'الفرع الافتراضي',
    costCenter: 'مركز التكلفة',
    tabOperational: 'بنود تشغيلية',
    tabAdministrative: 'مصروفات إدارية',
    tabAssets: 'أصول ثابتة',
    assetCategory: 'فئة الأصل',
    assetName: 'اسم الأصل',
    assetNameAr: 'اسم الأصل بالعربي',
    selectAssetCategory: 'اختر فئة الأصل',
    item: 'الصنف',
    selectItem: 'اختر الصنف...',
    unit: 'الوحدة',
    selectUnit: 'اختر الوحدة...',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    amount: 'المبلغ',
    category: 'فئة المصروف',
    selectCategory: 'اختر الفئة...',
    account: 'الحساب',
    receiptNumber: 'رقم الإيصال',
    receiptDate: 'تاريخ الإيصال',
    description: 'الوصف',
    addLine: 'إضافة سطر',
    totalExpenses: 'إجمالي المصروفات',
    returnedAmount: 'المبلغ المسترد',
    netSettlement: 'صافي التسوية',
    notes: 'ملاحظات',
    save: 'حفظ التسوية',
    cancel: 'إلغاء',
    required: 'مطلوب',
    optional: 'اختياري',
    saved: 'تم حفظ التسوية بنجاح',
    updated: 'تم تحديث التسوية بنجاح',
    saveFailed: 'فشل حفظ التسوية',
    balanceWarning: 'إجمالي المصروفات + المسترد يتجاوز رصيد العهدة!',
    noItems: 'أضف بند مصروف واحد على الأقل',
    kwd: 'KWD',
    dkw: 'د.ك',
    autoAccount: 'تلقائي من الفئة',
    autoCostCenter: 'تلقائي من الفرع',
    returnAccount: 'حساب الاسترداد',
    selectReturnAccount: 'اختر حساب الاسترداد...',
    returnAccountRequired: 'يجب اختيار حساب الاسترداد عند وجود مبلغ مسترد',
    permissionDenied: 'غير مصرح',
  },
};

// ============================================================
// Interfaces
// ============================================================
interface Props {
  open: boolean;
  onClose: () => void;
  settlement?: CustodySettlement;
  viewOnly?: boolean;
  onSave: () => void;
}

interface ItemUnit {
  itemUnitId?: number;
  itemId?: number;
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
  conversionFactor: number;
  defaultPurchasePrice: number;
  defaultSalePrice: number;
  isDefaultPurchase: boolean;
  isDefaultSale: boolean;
}

interface CategoryInfo {
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  expenseAccountId?: number;
  expenseAccountName?: string;
  expenseAccountNameAr?: string;
  expenseAccountCode?: string;
  subCategories?: CategoryInfo[];
}

interface CustodyOption {
  custodyId: number;
  custodyNumber: string;
  employeeName?: string;
  employeeNameAr?: string;
  branchId?: number;
  branchName?: string;
  branchNameAr?: string;
  currentBalance: number;
}

interface OperationalLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: ItemUnit | null;
  availableUnits: ItemUnit[];
  quantity: number;
  unitPrice: number;
  accountId: number | null;
  accountName: string;
  costCenterId: number | null;
  branchId: number | null;
  receiptNumber: string;
  receiptDate: string;
  description: string;
}

interface AdministrativeLine {
  id: string;
  categoryId: number | null;
  category: CustodyExpenseCategory | null;
  accountId: number | null;
  accountName: string;
  costCenterId: number | null;
  branchId: number | null;
  amount: number;
  receiptNumber: string;
  receiptDate: string;
  description: string;
}

interface AssetLine {
  id: string;
  fixedAssetCategoryId: number | null;
  fixedAssetCategoryName: string;
  assetName: string;
  assetNameAr: string;
  accountId: number | null;
  accountName: string;
  branchId: number | null;
  amount: number;
  receiptNumber: string;
  receiptDate: string;
  description: string;
}

interface FixedAssetCategoryOption {
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  assetType: string;
  defaultAssetAccountId: number | null;
}

interface AccountOption {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  isFinalAccount: boolean;
}

// الأدوار المقيدة بفرع — لا يرون عمود الفرع بكل سطر
const BRANCH_RESTRICTED_ROLES = ['Branch Manager', 'Warehouse Keeper', 'مدير فرع', 'أمين مخزن'];

// ============================================================
// Component
// ============================================================
const CustodySettlementDialog: React.FC<Props> = ({ open, onClose, settlement, viewOnly = false, onSave }) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDarkMode = theme.palette.mode === 'dark';

  // Colors — indigo theme
  const gradientMain = isDarkMode
    ? 'linear-gradient(135deg, #1a237e 0%, #0d1b3e 100%)'
    : 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)';
  const inputBg = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';

  // ========== State ==========
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!settlement;
  const dateLocked = (isEditMode && !isSuperAdmin) || viewOnly;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  // Form
  const [formData, setFormData] = useState({
    custodyId: null as number | null,
    settlementDate: new Date().toISOString().split('T')[0],
    returnedAmount: 0,
    returnAccountId: null as number | null,
    branchId: null as number | null,
    notes: '',
  });
  const [settlementNumber, setSettlementNumber] = useState('');

  // Selected objects
  const [selectedCustody, setSelectedCustody] = useState<CustodyOption | null>(null);
  const [branchCostCenterId, setBranchCostCenterId] = useState<number | null>(null);
  const [branchName, setBranchName] = useState('');
  const [custodyBalance, setCustodyBalance] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  // Lines
  const [operationalLines, setOperationalLines] = useState<OperationalLine[]>([]);
  const [administrativeLines, setAdministrativeLines] = useState<AdministrativeLine[]>([]);
  const [assetLines, setAssetLines] = useState<AssetLine[]>([]);

  // Master data
  const [custodies, setCustodies] = useState<CustodyOption[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<CustodyExpenseCategory[]>([]);
  const [itemCategories, setItemCategories] = useState<CategoryInfo[]>([]);
  const [fixedAssetCategories, setFixedAssetCategories] = useState<FixedAssetCategoryOption[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [selectedReturnAccount, setSelectedReturnAccount] = useState<AccountOption | null>(null);

  // JWT info
  const [userBranchIds, setUserBranchIds] = useState<number[]>([]);
  const [isBranchRestricted, setIsBranchRestricted] = useState(false);

  // عمود الفرع بكل سطر يظهر فقط لغير المقيدين (admin, محاسب, مندوب...)
  const showLineBranch = !isBranchRestricted;

  // الفروع المتاحة: المقيد يرى فروعه فقط — الباقي يرى الكل
  const availableBranches = isBranchRestricted
    ? branches.filter(b => userBranchIds.includes(b.branchId))
    : branches;

  // ========== Effects ==========
  useEffect(() => {
    if (open) {
      loadMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (open && custodies.length > 0 && items.length > 0) {
      if (settlement) {
        loadSettlement();
      } else {
        loadNewNumber();
        if (operationalLines.length === 0) addOperationalLine();
      }
    }
  }, [open, settlement, custodies, items]);

  // ========== Load Data ==========
  const loadMasterData = async () => {
    try {
      // Parse JWT
      const token = localStorage.getItem('token');
      let branchIds: number[] = [];
      let role = '';
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primary = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowed = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          branchIds = Array.from(new Set([...primary, ...allowed]));
          role = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '';
        } catch (e) { console.error('JWT parse error:', e); }
      }
      setUserBranchIds(branchIds);
      const restricted = BRANCH_RESTRICTED_ROLES.some(r => r.toLowerCase() === role.toLowerCase());
      setIsBranchRestricted(restricted);

      const [custodiesData, itemsData, branchesData, expCatData, catData, accountsData, fixedAssetCatData] = await Promise.all([
        axios.get('/Custodies/lookup'),
        itemsService.getAll(),
        branchesService.getAll(),
        expenseCategoryService.getActive(),
        axios.get('/Categories'),
        axios.get('/ChartOfAccounts'),
        axios.get('/FixedAssetCategories').catch(() => ({ data: [] })),
      ]);

      // حسابات — فقط الحسابات النهائية (IsFinalAccount = true)
      const acctResponse = (accountsData as any)?.data;
      const acctList = acctResponse?.data || acctResponse || [];
      const acctRaw = Array.isArray(acctList) ? acctList : [];
      setAccounts(acctRaw.filter((a: AccountOption) => a.isFinalAccount));

      // ✅ lookup endpoint يعالج الفلترة تلقائياً (حامل العهدة/مدير الفرع/الأدمن)
      const custList = Array.isArray(custodiesData) ? custodiesData : (custodiesData?.data || []);
      setCustodies(custList);

      setItems(itemsData);
      setBranches(branchesData);

      const expCat = Array.isArray(expCatData) ? expCatData : (expCatData?.data || []);
      setExpenseCategories(expCat);

      // Fixed Asset Categories (tangible only)
      const faCatRaw = Array.isArray(fixedAssetCatData) ? fixedAssetCatData : (fixedAssetCatData?.data || []);
      setFixedAssetCategories(faCatRaw.filter((c: any) => c.assetType === 'Tangible' && c.isActive !== false));

      // فلترة شجرة الفئات
      const catsRaw = Array.isArray(catData) ? catData : (catData?.data || []);
      const flatCats: CategoryInfo[] = [];
      const flattenCategories = (cats: any[]) => {
        for (const cat of cats) {
          flatCats.push(cat);
          if (cat.subCategories && cat.subCategories.length > 0) {
            flattenCategories(cat.subCategories);
          }
        }
      };
      flattenCategories(catsRaw);
      setItemCategories(flatCats);

      // اختيار تلقائي للمقيد بفرع واحد
      if (restricted && branchIds.length === 1) {
        const br = branchesData.find((b: Branch) => b.branchId === branchIds[0]);
        if (br) {
          setSelectedBranch(br);
          setBranchCostCenterId(br.costCenterId || null);
          setBranchName(isRTL ? (br.branchNameAr || br.branchName) : br.branchName);
          setFormData(prev => ({ ...prev, branchId: br.branchId }));
        }
      }
    } catch (error: any) {
      console.error('Failed to load master data:', error);
      if (error.response?.status === 403) {
        toast.error(t.permissionDenied, { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      }
    }
  };

  const loadNewNumber = async () => {
    try {
      const result = await custodySettlementService.generateNumber();
      const num = typeof result === 'string' ? result : (result?.data || '');
      setSettlementNumber(num);
    } catch (error) {
      console.error('Failed to generate number:', error);
    }
  };

  const loadSettlement = async () => {
    if (!settlement) return;

    setSettlementNumber(settlement.settlementNumber || '');

    // Set return account
    if (settlement.returnAccountId) {
      const acct = accounts.find((a: AccountOption) => a.accountId === settlement.returnAccountId);
      if (acct) setSelectedReturnAccount(acct);
    }

    // Set custody
    const cust = custodies.find(c => c.custodyId === settlement.custodyId);
    if (cust) {
      setSelectedCustody(cust);
      setCustodyBalance(cust.currentBalance || (settlement as any).custodyCurrentBalance || 0);
    }

    // Set branch
    if (settlement.branchId) {
      const branch = branches.find(b => b.branchId === settlement.branchId);
      if (branch) {
        setSelectedBranch(branch);
        setBranchCostCenterId(branch.costCenterId || null);
        setBranchName(isRTL ? (branch.branchNameAr || branch.branchName) : branch.branchName);
      }
    }

    setFormData({
      custodyId: settlement.custodyId,
      settlementDate: settlement.settlementDate?.split('T')[0] || '',
      returnedAmount: settlement.returnedAmount || 0,
      returnAccountId: settlement.returnAccountId || null,
      branchId: settlement.branchId || null,
      notes: settlement.notes || '',
    });

    // Load detail lines
    const opLines: OperationalLine[] = [];
    const adLines: AdministrativeLine[] = [];
    const astLines: AssetLine[] = [];

    for (const detail of (settlement.details || [])) {
      // استرجاع الفرع: أولاً من branchId المحفوظ، ثم من costCenterId، ثم الافتراضي
      let lineBranchId: number | null = detail.branchId ?? null;
      if (!lineBranchId && detail.costCenterId) {
        const brFromCC = branches.find(b => b.costCenterId === detail.costCenterId);
        if (brFromCC) lineBranchId = brFromCC.branchId;
      }
      if (!lineBranchId) lineBranchId = settlement.branchId ?? null;

      if (detail.expenseType === 'Item') {
        const item = items.find(i => i.itemId === detail.itemId);
        let availableUnits: ItemUnit[] = [];
        let selectedUnit: ItemUnit | null = null;

        if (detail.itemId) {
          try {
            availableUnits = await itemUnitsService.getItemUnits(detail.itemId);
            selectedUnit = availableUnits.find(u => u.unitId === detail.unitId) || null;
          } catch (e) { console.error('Failed to load units:', e); }
        }

        opLines.push({
          id: `${detail.detailId}`,
          itemId: detail.itemId || null,
          item: item || null,
          unitId: detail.unitId || null,
          unit: selectedUnit,
          availableUnits,
          quantity: detail.quantity || 0,
          unitPrice: detail.unitPrice || 0,
          accountId: detail.accountId,
          accountName: isRTL ? (detail.accountNameAr || detail.accountName || '') : (detail.accountName || ''),
          costCenterId: detail.costCenterId || null,
          branchId: lineBranchId,
          receiptNumber: detail.receiptNumber || '',
          receiptDate: detail.receiptDate?.split('T')[0] || '',
          description: detail.description || '',
        });
      } else if (detail.expenseType === 'Asset') {
        astLines.push({
          id: `${detail.detailId}`,
          fixedAssetCategoryId: detail.fixedAssetCategoryId || null,
          fixedAssetCategoryName: isRTL ? (detail.fixedAssetCategoryNameAr || detail.fixedAssetCategoryName || '') : (detail.fixedAssetCategoryName || ''),
          assetName: detail.assetName || '',
          assetNameAr: detail.assetNameAr || '',
          accountId: detail.accountId,
          accountName: isRTL ? (detail.accountNameAr || detail.accountName || '') : (detail.accountName || ''),
          branchId: lineBranchId,
          amount: detail.amount,
          receiptNumber: detail.receiptNumber || '',
          receiptDate: detail.receiptDate?.split('T')[0] || '',
          description: detail.description || '',
        });
      } else {
        const cat = expenseCategories.find(c => c.categoryId === detail.categoryId);
        adLines.push({
          id: `${detail.detailId}`,
          categoryId: detail.categoryId || null,
          category: cat || null,
          accountId: detail.accountId,
          accountName: isRTL ? (detail.accountNameAr || detail.accountName || '') : (detail.accountName || ''),
          costCenterId: detail.costCenterId || null,
          branchId: lineBranchId,
          amount: detail.amount,
          receiptNumber: detail.receiptNumber || '',
          receiptDate: detail.receiptDate?.split('T')[0] || '',
          description: detail.description || '',
        });
      }
    }

    setOperationalLines(opLines.length > 0 ? opLines : []);
    setAdministrativeLines(adLines.length > 0 ? adLines : []);
    setAssetLines(astLines.length > 0 ? astLines : []);
  };

  // ========== Default Branch Selection ==========
  const handleBranchSelect = (branch: Branch | null) => {
    setSelectedBranch(branch);
    if (branch) {
      setBranchCostCenterId(branch.costCenterId || null);
      setBranchName(isRTL ? (branch.branchNameAr || branch.branchName) : branch.branchName);
      setFormData(prev => ({ ...prev, branchId: branch.branchId }));
    } else {
      setBranchCostCenterId(null);
      setBranchName('');
      setFormData(prev => ({ ...prev, branchId: null }));
    }
  };

  // ========== Per-line Branch Change — FIX: لا يرجع للفرع الافتراضي ==========
  const handleLineBranchChange = (lineId: string, lineType: 'op' | 'ad', branch: Branch | null) => {
    // إذا اختار فرع → يأخذ costCenterId من الفرع (حتى لو null كالشركة الرئيسية)
    // إذا مسح الفرع → يرجع للافتراضي
    const costCenterId: number | null = branch ? (branch.costCenterId ?? null) : branchCostCenterId;
    const brId: number | null = branch ? branch.branchId : null;

    if (lineType === 'op') {
      setOperationalLines(prev => prev.map(l => l.id === lineId
        ? { ...l, branchId: brId, costCenterId }
        : l));
    } else {
      setAdministrativeLines(prev => prev.map(l => l.id === lineId
        ? { ...l, branchId: brId, costCenterId }
        : l));
    }
  };

  // ========== Custody Selection ==========
  const handleCustodyChange = (custody: CustodyOption | null) => {
    setSelectedCustody(custody);
    if (custody) {
      setFormData(prev => ({ ...prev, custodyId: custody.custodyId }));
      setCustodyBalance(custody.currentBalance || 0);
    } else {
      setFormData(prev => ({ ...prev, custodyId: null }));
      setCustodyBalance(0);
    }
  };

  // ========== Operational Item Handlers ==========
  const addOperationalLine = () => {
    setOperationalLines(prev => [...prev, {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      itemId: null, item: null, unitId: null, unit: null, availableUnits: [],
      quantity: 1, unitPrice: 0, accountId: null, accountName: '',
      costCenterId: branchCostCenterId,
      branchId: selectedBranch?.branchId || null,
      receiptNumber: '', receiptDate: '', description: '',
    }]);
  };

  const removeOperationalLine = (id: string) => {
    setOperationalLines(prev => prev.filter(l => l.id !== id));
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      setOperationalLines(prev => prev.map(l => l.id === lineId ? {
        ...l, item: null, itemId: null, unit: null, unitId: null,
        availableUnits: [], unitPrice: 0, accountId: null, accountName: '',
      } : l));
      return;
    }

    try {
      let units = await itemUnitsService.getItemUnits(item.itemId);

      const hasBaseUnit = units.some((u: ItemUnit) => u.unitId === item.baseUnitId);
      if (!hasBaseUnit && item.baseUnitId) {
        try {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find((u: any) => u.unitId === item.baseUnitId);
          if (baseUnit) {
            units = [{
              itemUnitId: 0, itemId: item.itemId, unitId: baseUnit.unitId,
              unitName: baseUnit.unitName, unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode, conversionFactor: 1,
              defaultSalePrice: 0, defaultPurchasePrice: 0,
              isDefaultSale: true, isDefaultPurchase: true,
            }, ...units];
          }
        } catch (e) { /* ignore */ }
      }

      let defaultUnit = units.find((u: ItemUnit) => u.unitId === item.baseUnitId);
      if (!defaultUnit) defaultUnit = units.find((u: ItemUnit) => u.isDefaultPurchase);
      if (!defaultUnit) defaultUnit = units[0] || null;

      let accountId: number | null = null;
      let accountName = '';
      if ((item as any).categoryId) {
        const cat = itemCategories.find(c => c.categoryId === (item as any).categoryId);
        if (cat && cat.expenseAccountId) {
          accountId = cat.expenseAccountId;
          accountName = isRTL
            ? `${cat.expenseAccountCode || ''} - ${cat.expenseAccountNameAr || cat.expenseAccountName || ''}`
            : `${cat.expenseAccountCode || ''} - ${cat.expenseAccountName || ''}`;
        }
      }

      const unitPrice = defaultUnit?.defaultPurchasePrice || 0;

      setOperationalLines(prev => prev.map(l => l.id === lineId ? {
        ...l,
        item, itemId: item.itemId,
        availableUnits: units,
        unit: defaultUnit, unitId: defaultUnit?.unitId || null,
        unitPrice,
        accountId, accountName,
        costCenterId: l.costCenterId || branchCostCenterId,
      } : l));
    } catch (error) {
      console.error('Failed to load item data:', error);
    }
  };

  const handleUnitChange = (lineId: string, unit: ItemUnit | null) => {
    if (!unit) return;
    const line = operationalLines.find(l => l.id === lineId);
    if (!line) return;

    const unitPrice = unit.defaultPurchasePrice || line.unitPrice || 0;
    setOperationalLines(prev => prev.map(l => l.id === lineId ? {
      ...l, unit, unitId: unit.unitId, unitPrice,
    } : l));
  };

  const updateOpLine = (id: string, field: string, value: any) => {
    setOperationalLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // ========== Administrative Handlers ==========
  const addAdministrativeLine = () => {
    setAdministrativeLines(prev => [...prev, {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      categoryId: null, category: null,
      accountId: null, accountName: '',
      costCenterId: branchCostCenterId,
      branchId: selectedBranch?.branchId || null,
      amount: 0, receiptNumber: '', receiptDate: '', description: '',
    }]);
  };

  const removeAdministrativeLine = (id: string) => {
    setAdministrativeLines(prev => prev.filter(l => l.id !== id));
  };

  const handleCategorySelect = (lineId: string, cat: CustodyExpenseCategory | null) => {
    if (!cat) {
      setAdministrativeLines(prev => prev.map(l => l.id === lineId ? {
        ...l, category: null, categoryId: null, accountId: null, accountName: '',
      } : l));
      return;
    }

    const accountName = isRTL
      ? `${cat.accountCode || ''} - ${cat.accountNameAr || cat.accountName || ''}`
      : `${cat.accountCode || ''} - ${cat.accountName || ''}`;

    setAdministrativeLines(prev => prev.map(l => l.id === lineId ? {
      ...l,
      category: cat, categoryId: cat.categoryId,
      accountId: cat.accountId, accountName,
      // Admin: no branch, no cost center
      // Operational/Bakery: reset branch to force re-selection
      branchId: cat.branchType === 'Admin' ? null : l.branchId,
      costCenterId: cat.branchType === 'Admin' ? null : (l.costCenterId || branchCostCenterId),
    } : l));
  };

  // Filter branches based on category's BranchType
  const getBranchesForCategory = (cat: CustodyExpenseCategory | null): any[] => {
    if (!cat?.branchType || cat.branchType === 'Admin') return [];
    if (cat.branchType === 'Operational') return branches.filter((b: any) => b.branchType === 'Operational');
    if (cat.branchType === 'Bakery') return branches.filter((b: any) => b.branchType === 'Warehouse');
    return branches;
  };

  const updateAdLine = (id: string, field: string, value: any) => {
    setAdministrativeLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // ========== Calculations ==========
  const opTotal = operationalLines.reduce((sum, l) => sum + (l.quantity * l.unitPrice), 0);
  const adTotal = administrativeLines.reduce((sum, l) => sum + l.amount, 0);
  const assetTotal = assetLines.reduce((sum, l) => sum + l.amount, 0);
  const totalExpenses = opTotal + adTotal + assetTotal;
  const netSettlement = totalExpenses + formData.returnedAmount;
  const exceedsBalance = selectedCustody ? netSettlement > custodyBalance : false;

  // ========== Validation & Save ==========
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.custodyId) errs.custodyId = t.required;
    if (!formData.settlementDate) errs.settlementDate = t.required;

    if (formData.returnedAmount > 0 && !formData.returnAccountId) {
      toast.error(t.returnAccountRequired, { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      return false;
    }

    const allLines = [
      ...operationalLines.filter(l => l.itemId),
      ...administrativeLines.filter(l => l.categoryId),
      ...assetLines.filter(l => l.fixedAssetCategoryId),
    ];
    if (allLines.length === 0) {
      toast.error(t.noItems, { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      return false;
    }

    operationalLines.forEach((l, i) => {
      if (!l.itemId) errs[`op_item_${i}`] = t.required;
      if (!l.unitId) errs[`op_unit_${i}`] = t.required;
      if (l.quantity <= 0) errs[`op_qty_${i}`] = '> 0';
    });

    administrativeLines.forEach((l, i) => {
      if (!l.categoryId) errs[`ad_cat_${i}`] = t.required;
      if (l.amount <= 0) errs[`ad_amt_${i}`] = '> 0';
    });

    assetLines.forEach((l, i) => {
      if (!l.fixedAssetCategoryId) errs[`ast_cat_${i}`] = t.required;
      if (!l.assetName && !l.assetNameAr) errs[`ast_name_${i}`] = t.required;
      if (l.amount <= 0) errs[`ast_amt_${i}`] = '> 0';
      if (!l.accountId) errs[`ast_acct_${i}`] = t.required;
    });

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields',
        { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const details: CreateCustodySettlementDetail[] = [];
      let lineNum = 1;

      // Filter out empty operational lines (no item selected)
      operationalLines.filter(l => l.itemId).forEach(l => {
        details.push({
          lineNumber: lineNum++,
          expenseType: 'Item',
          itemId: l.itemId!,
          unitId: l.unitId!,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          accountId: l.accountId!,
          costCenterId: l.costCenterId || undefined,
          branchId: l.branchId || undefined,
          amount: l.quantity * l.unitPrice,
          receiptNumber: l.receiptNumber || undefined,
          receiptDate: l.receiptDate || undefined,
          description: l.description || undefined,
        });
      });

      // Filter out empty admin lines (no category selected)
      administrativeLines.filter(l => l.categoryId).forEach(l => {
        details.push({
          lineNumber: lineNum++,
          expenseType: 'Category',
          categoryId: l.categoryId!,
          accountId: l.accountId!,
          costCenterId: l.costCenterId || undefined,
          branchId: l.branchId || undefined,
          amount: l.amount,
          receiptNumber: l.receiptNumber || undefined,
          receiptDate: l.receiptDate || undefined,
          description: l.description || undefined,
        });
      });

      // Filter out empty asset lines (no category selected)
      assetLines.filter(l => l.fixedAssetCategoryId).forEach(l => {
        details.push({
          lineNumber: lineNum++,
          expenseType: 'Asset',
          fixedAssetCategoryId: l.fixedAssetCategoryId!,
          assetName: l.assetName,
          assetNameAr: l.assetNameAr,
          accountId: l.accountId!,
          branchId: l.branchId || undefined,
          amount: l.amount,
          receiptNumber: l.receiptNumber || undefined,
          receiptDate: l.receiptDate || undefined,
          description: l.description || undefined,
        });
      });

      if (settlement) {
        const dto: UpdateCustodySettlement = {
          settlementId: settlement.settlementId,
          settlementDate: formData.settlementDate,
          returnedAmount: formData.returnedAmount,
          returnAccountId: formData.returnAccountId || undefined,
          branchId: formData.branchId || undefined,
          notes: formData.notes || undefined,
          details,
        };
        await custodySettlementService.update(settlement.settlementId, dto);
        toast.success(t.updated, { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      } else {
        const dto: CreateCustodySettlement = {
          custodyId: formData.custodyId!,
          settlementDate: formData.settlementDate,
          returnedAmount: formData.returnedAmount,
          returnAccountId: formData.returnAccountId || undefined,
          branchId: formData.branchId || undefined,
          notes: formData.notes || undefined,
          details,
        };
        await custodySettlementService.create(dto);
        toast.success(t.saved, { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.message || t.saveFailed,
        { position: 'top-center', style: { fontSize: 16, fontWeight: 'bold' } });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ custodyId: null, settlementDate: new Date().toISOString().split('T')[0], returnedAmount: 0, returnAccountId: null, branchId: null, notes: '' });
    setSelectedReturnAccount(null);
    setSettlementNumber('');
    setSelectedCustody(null);
    setSelectedBranch(null);
    setBranchCostCenterId(null);
    setBranchName('');
    setCustodyBalance(0);
    setOperationalLines([]);
    setAdministrativeLines([]);
    setErrors({});
    setActiveTab(0);
    onClose();
  };

  // ========== Input style helper ==========
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      bgcolor: inputBg,
    },
  };

  const whiteInputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.15)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' },
    '& .MuiInputBase-input': { color: 'white' },
  };

  const headerBg = isDarkMode ? '#1a1a2e' : alpha('#5c6bc0', 0.08);

  const currency = isRTL ? t.dkw : t.kwd;

  // ============================================================
  // RENDER
  // ============================================================
  const title = viewOnly ? t.viewTitle : settlement ? t.editTitle : t.addTitle;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg"
      PaperProps={{ sx: { minHeight: '99vh', maxHeight: '99vh', borderRadius: 3,
        bgcolor: isDarkMode ? '#0f172a' : '#fafafa' } }}>

      {/* ===== Header ===== */}
      <Box sx={{ background: gradientMain, color: 'white', p: 1,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ReceiptIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={800}>{title}</Typography>
            {settlementNumber && (
              <Chip label={settlementNumber} size="small"
                sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {settlement?.status && (
            <Chip label={settlement.status} size="small"
              sx={{ bgcolor: settlement.status === 'Approved' ? '#4caf50' : '#ff9800',
                color: 'white', fontWeight: 700 }} />
          )}
          <IconButton onClick={handleClose} sx={{ color: 'white' }}><Close /></IconButton>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ bgcolor: '#5c6bc0' }} />}

      <DialogContent sx={{ p: 3 }}>
        {/* --- Basic Info Card --- */}
        <Card elevation={4} sx={{ mb: 3, background: gradientMain, color: 'white', position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: -50, left: -50, width: 180, height: 180,
            background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', borderRadius: '50%' },
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <PersonIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>{t.basicInfo}</Typography>
            </Stack>

            {/* Row: Default Branch + Custody + Date + Balance */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>

              {/* Default Branch — يظهر دائماً لكل المستخدمين */}
              <Autocomplete
                options={availableBranches}
                getOptionLabel={(o: Branch) => `${o.branchCode} - ${isRTL ? (o.branchNameAr || o.branchName) : o.branchName}`}
                value={selectedBranch}
                onChange={(_, v) => handleBranchSelect(v)}
                disabled={viewOnly || !!settlement || (isBranchRestricted && userBranchIds.length === 1)}
                renderInput={(params) => (
                  <TextField {...params} label={t.defaultBranch}
                    placeholder={t.selectBranch}
                    sx={whiteInputSx} />
                )}
              />

              {/* Custody Selector */}
              <Autocomplete
                options={custodies}
                getOptionLabel={(o: CustodyOption) => {
                  const name = isRTL ? (o.employeeNameAr || o.employeeName || '') : (o.employeeName || '');
                  return `${o.custodyNumber} - ${name} (${(o.currentBalance || 0).toFixed(3)} ${currency})`;
                }}
                value={selectedCustody}
                onChange={(_, v) => handleCustodyChange(v)}
                disabled={viewOnly || !!settlement}
                renderInput={(params) => (
                  <TextField {...params} label={t.custody} error={!!errors.custodyId}
                    sx={whiteInputSx} />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Box sx={{ width: '100%' }}>
                        <Typography fontWeight="bold">{option.custodyNumber}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isRTL ? (option.employeeNameAr || option.employeeName) : option.employeeName}
                          {' — '}{(option.currentBalance || 0).toFixed(3)} {currency}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
              />

              {/* Settlement Date */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.settlementDate}
                  value={formData.settlementDate ? new Date(formData.settlementDate) : null}
                  onChange={(date) => setFormData(prev => ({ ...prev, settlementDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' }))}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  disabled={dateLocked}
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.settlementDate, sx: whiteInputSx } }}
                />
              </LocalizationProvider>

              {/* Current Balance */}
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.currentBalance}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700,
                  color: custodyBalance > 0 ? '#81c784' : '#ef5350' }}>
                  {custodyBalance.toFixed(3)} <small>{currency}</small>
                </Typography>
              </Box>
            </Box>

            {/* Branch & Cost Center Chips */}
            {branchName && (
              <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                <Chip icon={<BranchIcon sx={{ color: 'white !important' }} />}
                  label={`${t.branch}: ${branchName}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                {branchCostCenterId && (
                  <Chip icon={<AccountIcon sx={{ color: 'white !important' }} />}
                    label={`${t.costCenter}: #${branchCostCenterId}`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                )}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Balance Warning */}
        {exceedsBalance && (
          <Alert severity="warning" sx={{ mb: 2, fontWeight: 'bold' }}>{t.balanceWarning}</Alert>
        )}

        {/* --- Tabs --- */}
        <Paper elevation={3} sx={{ borderRadius: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600, fontSize: 15 },
              '& .Mui-selected': { color: '#5c6bc0 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#5c6bc0', height: 3 },
            }}>
            <Tab label={`${t.tabOperational} (${operationalLines.length})`} />
            <Tab label={`${t.tabAdministrative} (${administrativeLines.length})`} />
            <Tab label={`${t.tabAssets} (${assetLines.length})`} />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* ===== TAB 0: Operational Items ===== */}
            {activeTab === 0 && (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button startIcon={<Add />} onClick={addOperationalLine} disabled={viewOnly}
                    variant="contained" size="small"
                    sx={{ bgcolor: '#5c6bc0', '&:hover': { bgcolor: '#3949ab' } }}>
                    {t.addLine}
                  </Button>
                </Stack>

                <Box ref={gridRef}>
                <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: showLineBranch ? 1600 : 1250 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 280 }}>{t.item}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 230 }}>{t.unit}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 100 }}>{t.quantity}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 120 }}>{t.unitPrice}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 170 }}>{t.amount}</TableCell>
                        {showLineBranch && (
                          <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 280 }}>{t.branch}</TableCell>
                        )}
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 130 }}>{t.receiptNumber}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 180 }}>{t.description}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 50 }}>{''}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operationalLines.map((line, idx) => (
                        <TableRow key={line.id} sx={{ '&:hover': { bgcolor: alpha('#5c6bc0', 0.04) } }}>
                          {/* Item */}
                          <TableCell sx={{ minWidth: 280 }}>
                            <Autocomplete size="small" options={items}
                              getOptionLabel={(o: Item) => `${o.itemCode} - ${isRTL ? o.itemNameAr : o.itemName}`}
                              value={line.item} disabled={viewOnly}
                              onChange={(_, v) => handleItemChange(line.id, v)}
                              renderInput={(params) => (
                                <TextField {...params} placeholder={t.selectItem}
                                  error={!!errors[`op_item_${idx}`]} variant="outlined" sx={inputSx} />
                              )} />
                          </TableCell>
                          {/* Unit */}
                          <TableCell sx={{ minWidth: 230 }}>
                            <Autocomplete size="small" options={line.availableUnits}
                              getOptionLabel={(o: ItemUnit) => `${o.unitCode} - ${isRTL ? o.unitNameAr : o.unitName}`}
                              value={line.unit} disabled={viewOnly || !line.item}
                              onChange={(_, v) => handleUnitChange(line.id, v)}
                              renderInput={(params) => (
                                <TextField {...params} placeholder={t.selectUnit}
                                  error={!!errors[`op_unit_${idx}`]} variant="outlined" sx={inputSx} />
                              )} />
                          </TableCell>
                          {/* Qty */}
                          <TableCell sx={{ minWidth: 100 }}>
                            <TextField size="small" variant="outlined"
                              value={line.quantity || ''}
                              onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateOpLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                              disabled={viewOnly} error={!!errors[`op_qty_${idx}`]} inputProps={{ inputMode: 'decimal' }} sx={inputSx} />
                          </TableCell>
                          {/* Price */}
                          <TableCell sx={{ minWidth: 120 }}>
                            <TextField size="small" variant="outlined"
                              value={line.unitPrice || ''}
                              onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateOpLine(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                              disabled={viewOnly} inputProps={{ inputMode: 'decimal' }} sx={inputSx} />
                          </TableCell>
                          {/* Amount */}
                          <TableCell sx={{ minWidth: 170 }}>
                            <Chip label={`${(line.quantity * line.unitPrice).toFixed(3)}`}
                              size="small" color="primary" variant="outlined"
                              sx={{ fontWeight: 800, fontSize: 14, minWidth: 120 }} />
                            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 600 }}>{currency}</Typography>
                          </TableCell>
                          {/* Branch per line — فقط لغير المقيدين */}
                          {showLineBranch && (
                            <TableCell sx={{ minWidth: 280 }}>
                              <Autocomplete size="small" options={branches}
                                getOptionLabel={(o: Branch) => `${o.branchCode} - ${isRTL ? (o.branchNameAr || o.branchName) : o.branchName}`}
                                value={branches.find(b => b.branchId === line.branchId) || selectedBranch || null}
                                disabled={viewOnly}
                                onChange={(_, v) => handleLineBranchChange(line.id, 'op', v)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder={t.selectBranch} variant="outlined" sx={inputSx} />
                                )} />
                            </TableCell>
                          )}
                          {/* Receipt # */}
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField size="small" variant="outlined" value={line.receiptNumber}
                              onChange={(e) => updateOpLine(line.id, 'receiptNumber', e.target.value)}
                              disabled={viewOnly} sx={inputSx} />
                          </TableCell>
                          {/* Description */}
                          <TableCell sx={{ minWidth: 180 }}>
                            <TextField size="small" variant="outlined" value={line.description}
                              onChange={(e) => updateOpLine(line.id, 'description', e.target.value)}
                              disabled={viewOnly} sx={inputSx} />
                          </TableCell>
                          {/* Delete */}
                          <TableCell sx={{ minWidth: 50 }}>
                            {!viewOnly && (
                              <IconButton size="small" color="error"
                                onClick={() => removeOperationalLine(line.id)}
                                sx={{ '&:hover': { transform: 'scale(1.15)', transition: 'all 0.2s' } }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>

                {operationalLines.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 1 }}>
                    <Chip label={`${isRTL ? 'إجمالي التشغيلي' : 'Operational Total'}: ${opTotal.toFixed(3)} ${currency}`}
                      color="primary" sx={{ fontWeight: 700 }} />
                  </Box>
                )}
              </>
            )}

            {/* ===== TAB 1: Administrative Expenses ===== */}
            {activeTab === 1 && (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button startIcon={<Add />} onClick={addAdministrativeLine} disabled={viewOnly}
                    variant="contained" size="small"
                    sx={{ bgcolor: '#5c6bc0', '&:hover': { bgcolor: '#3949ab' } }}>
                    {t.addLine}
                  </Button>
                </Stack>

                <Box ref={gridRef}>
                <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: showLineBranch ? 1300 : 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 280 }}>{t.category}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 180 }}>{t.amount}</TableCell>
                        {showLineBranch && (
                          <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 280 }}>{t.branch}</TableCell>
                        )}
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 130 }}>{t.receiptNumber}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 140 }}>{t.receiptDate}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 180 }}>{t.description}</TableCell>
                        <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 13, minWidth: 50 }}>{''}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {administrativeLines.map((line, idx) => (
                        <TableRow key={line.id} sx={{ '&:hover': { bgcolor: alpha('#5c6bc0', 0.04) } }}>
                          {/* Category */}
                          <TableCell sx={{ minWidth: 280 }}>
                            <Autocomplete size="small" options={expenseCategories}
                              getOptionLabel={(o: CustodyExpenseCategory) => isRTL ? o.categoryNameAr : o.categoryName}
                              value={line.category} disabled={viewOnly}
                              onChange={(_, v) => handleCategorySelect(line.id, v)}
                              renderInput={(params) => (
                                <TextField {...params} placeholder={t.selectCategory}
                                  error={!!errors[`ad_cat_${idx}`]} variant="outlined" sx={inputSx} />
                              )} />
                          </TableCell>
                          {/* Amount */}
                          <TableCell sx={{ minWidth: 180 }}>
                            <TextField size="small" variant="outlined"
                              value={line.amount || ''}
                              onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateAdLine(line.id, 'amount', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                              disabled={viewOnly} error={!!errors[`ad_amt_${idx}`]}
                              inputProps={{ inputMode: 'decimal' }}
                              InputProps={{ endAdornment: <InputAdornment position="end"><small>{currency}</small></InputAdornment> }}
                              sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 15, fontWeight: 700 } }} />
                          </TableCell>
                          {/* Branch per line — hidden for Admin, filtered for Operational/Bakery */}
                          {showLineBranch && line.category?.branchType !== 'Admin' && (
                            <TableCell sx={{ minWidth: 280 }}>
                              <Autocomplete size="small"
                                options={getBranchesForCategory(line.category)}
                                getOptionLabel={(o: Branch) => `${o.branchCode} - ${isRTL ? (o.branchNameAr || o.branchName) : o.branchName}`}
                                value={branches.find(b => b.branchId === line.branchId) || null}
                                disabled={viewOnly}
                                onChange={(_, v) => handleLineBranchChange(line.id, 'ad', v)}
                                renderInput={(params) => (
                                  <TextField {...params}
                                    placeholder={line.category?.branchType === 'Bakery'
                                      ? (isRTL ? 'اختر فرع المخبز' : 'Select Bakery Branch')
                                      : t.selectBranch}
                                    variant="outlined" sx={inputSx} />
                                )} />
                            </TableCell>
                          )}
                          {showLineBranch && line.category?.branchType === 'Admin' && (
                            <TableCell sx={{ minWidth: 280 }}>
                              <TextField size="small" variant="outlined" disabled
                                value={isRTL ? 'لا يحتاج فرع' : 'No branch needed'}
                                sx={{ ...inputSx, '& .MuiInputBase-input': { color: 'text.disabled', fontStyle: 'italic' } }} />
                            </TableCell>
                          )}
                          {/* Receipt # */}
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField size="small" variant="outlined" value={line.receiptNumber}
                              onChange={(e) => updateAdLine(line.id, 'receiptNumber', e.target.value)}
                              disabled={viewOnly} sx={inputSx} />
                          </TableCell>
                          {/* Receipt Date */}
                          <TableCell sx={{ minWidth: 140 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={line.receiptDate ? new Date(line.receiptDate) : null}
                                onChange={(date) => updateAdLine(line.id, 'receiptDate', date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                format="dd/MM/yyyy"
                                views={['year', 'month', 'day']}
                                openTo="day"
                                disabled={viewOnly}
                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: inputSx } }}
                              />
                            </LocalizationProvider>
                          </TableCell>
                          {/* Description */}
                          <TableCell sx={{ minWidth: 180 }}>
                            <TextField size="small" variant="outlined" value={line.description}
                              onChange={(e) => updateAdLine(line.id, 'description', e.target.value)}
                              disabled={viewOnly} sx={inputSx} />
                          </TableCell>
                          {/* Delete */}
                          <TableCell sx={{ minWidth: 50 }}>
                            {!viewOnly && (
                              <IconButton size="small" color="error"
                                onClick={() => removeAdministrativeLine(line.id)}
                                sx={{ '&:hover': { transform: 'scale(1.15)', transition: 'all 0.2s' } }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>

                {administrativeLines.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 1 }}>
                    <Chip label={`${isRTL ? 'إجمالي الإداري' : 'Admin Total'}: ${adTotal.toFixed(3)} ${currency}`}
                      color="secondary" sx={{ fontWeight: 700 }} />
                  </Box>
                )}
              </>
            )}

            {/* ═══ Tab 2: Fixed Assets ═══ */}
            {activeTab === 2 && (
              <>
                {!viewOnly && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="contained" size="small" startIcon={<AddCircleOutlineIcon />}
                      onClick={() => setAssetLines(prev => [...prev, {
                        id: `ast_${Date.now()}`, fixedAssetCategoryId: null, fixedAssetCategoryName: '',
                        assetName: '', assetNameAr: '', accountId: null, accountName: '',
                        branchId: formData.branchId, amount: 0, receiptNumber: '', receiptDate: '', description: '',
                      }])}
                      sx={{ borderRadius: 2, background: gradientMain }}>
                      {isRTL ? 'إضافة أصل' : 'Add Asset'}
                    </Button>
                  </Box>
                )}

                {assetLines.map((line, idx) => (
                  <Paper key={line.id} elevation={1} sx={{ p: 2, mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                      {/* Asset Category */}
                      <Autocomplete
                        size="small"
                        options={fixedAssetCategories}
                        getOptionLabel={(opt) => isRTL ? opt.categoryNameAr : opt.categoryName}
                        value={fixedAssetCategories.find(c => c.categoryId === line.fixedAssetCategoryId) || null}
                        onChange={(_, val) => {
                          setAssetLines(prev => prev.map(l => l.id === line.id ? {
                            ...l,
                            fixedAssetCategoryId: val?.categoryId || null,
                            fixedAssetCategoryName: val ? (isRTL ? val.categoryNameAr : val.categoryName) : '',
                            accountId: val?.defaultAssetAccountId || null,
                            accountName: '',
                          } : l));
                        }}
                        renderInput={(params) => <TextField {...params} label={t.assetCategory} error={!!errors[`ast_cat_${idx}`]} />}
                        disabled={viewOnly}
                      />
                      {/* Asset Name EN */}
                      <TextField size="small" label={t.assetName} value={line.assetName}
                        onChange={e => setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, assetName: e.target.value } : l))}
                        error={!!errors[`ast_name_${idx}`]} disabled={viewOnly} />
                      {/* Asset Name AR */}
                      <TextField size="small" label={t.assetNameAr} value={line.assetNameAr}
                        onChange={e => setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, assetNameAr: e.target.value } : l))}
                        disabled={viewOnly} />
                      {/* Amount */}
                      <TextField size="small" label={t.amount} value={line.amount || ''}
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, amount: v === '' || v === '.' ? 0 : parseFloat(v) } : l)); }}
                        error={!!errors[`ast_amt_${idx}`]} disabled={viewOnly}
                        inputProps={{ inputMode: 'decimal' }} />
                      {/* Branch (location of asset — no cost center) */}
                      {!isBranchRestricted && (
                        <Autocomplete
                          size="small"
                          options={branches}
                          getOptionLabel={(b: any) => isRTL ? b.branchNameAr : b.branchName}
                          value={branches.find((b: any) => b.branchId === line.branchId) || null}
                          onChange={(_, val: any) => setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, branchId: val?.branchId || null } : l))}
                          renderInput={(params) => <TextField {...params} label={isRTL ? 'موقع الأصل' : 'Asset Location'} />}
                          disabled={viewOnly}
                        />
                      )}
                      {/* Receipt */}
                      <TextField size="small" label={t.receiptNumber} value={line.receiptNumber}
                        onChange={e => setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, receiptNumber: e.target.value } : l))}
                        disabled={viewOnly} />
                      {/* Description */}
                      <TextField size="small" label={t.description} value={line.description}
                        onChange={e => setAssetLines(prev => prev.map(l => l.id === line.id ? { ...l, description: e.target.value } : l))}
                        disabled={viewOnly} />
                    </Box>
                    {!viewOnly && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <IconButton size="small" color="error"
                          onClick={() => setAssetLines(prev => prev.filter(l => l.id !== line.id))}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                ))}

                {assetLines.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 1 }}>
                    <Chip label={`${isRTL ? 'إجمالي الأصول' : 'Assets Total'}: ${assetTotal.toFixed(3)} ${currency}`}
                      color="success" sx={{ fontWeight: 700 }} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>

        {/* --- Footer: Totals & Notes --- */}
        <Card elevation={4} sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: formData.returnedAmount > 0 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              {/* Total Expenses */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#5c6bc0', 0.08), textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{t.totalExpenses}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#5c6bc0' }}>
                  {totalExpenses.toFixed(3)} <small>{currency}</small>
                </Typography>
              </Box>

              {/* Returned Amount */}
              <TextField label={t.returnedAmount} type="number" variant="outlined"
                value={formData.returnedAmount || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, returnedAmount: parseFloat(e.target.value) || 0 }))}
                disabled={viewOnly}
                inputProps={{ min: 0, step: 0.001 }}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }}
                sx={inputSx} />

              {/* Return Account — يظهر عندما المسترد > 0 */}
              {formData.returnedAmount > 0 && (
                <Autocomplete
                  options={accounts}
                  getOptionLabel={(o: AccountOption) => `${o.accountCode} - ${isRTL ? o.accountNameAr : o.accountName}`}
                  value={selectedReturnAccount}
                  onChange={(_, v) => {
                    setSelectedReturnAccount(v);
                    setFormData(prev => ({ ...prev, returnAccountId: v?.accountId || null }));
                  }}
                  disabled={viewOnly}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t.returnAccount}
                      placeholder={t.selectReturnAccount}
                      error={formData.returnedAmount > 0 && !formData.returnAccountId}
                      sx={inputSx} />
                  )}
                />
              )}

              {/* Net Settlement */}
              <Box sx={{ p: 2, borderRadius: 2,
                bgcolor: exceedsBalance ? alpha('#ef5350', 0.1) : alpha('#4caf50', 0.08),
                textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{t.netSettlement}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700,
                  color: exceedsBalance ? '#ef5350' : '#4caf50' }}>
                  {netSettlement.toFixed(3)} <small>{currency}</small>
                </Typography>
              </Box>
            </Box>

            {/* Notes */}
            <TextField fullWidth label={t.notes} variant="outlined" multiline rows={2}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              disabled={viewOnly} sx={inputSx} />
          </CardContent>
        </Card>
      </DialogContent>

      {/* ===== Actions ===== */}
      {!viewOnly && (
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider',
          bgcolor: isDarkMode ? '#1e293b' : 'white' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
              {t.cancel}
            </Button>
            <Button onClick={handleSave} variant="contained" size="large" disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              sx={{ minWidth: 200, background: 'linear-gradient(45deg, #5c6bc0 30%, #3949ab 90%)',
                '&:hover': { background: 'linear-gradient(45deg, #3949ab 30%, #283593 90%)' } }}>
              {t.save}
            </Button>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

export default CustodySettlementDialog;